import { DateTime } from "luxon";
import type { AdminGetRoutesResDto } from "service/api/estabrookSchemas";
import { isSkippedStop } from "./stop-service-minutes";

/** Use the scheduled day so a live shift keeps its origin after midnight. */
export const getRouteStart = (startTime: string, date: string): DateTime => {
  const time = DateTime.fromFormat(startTime ?? "", "HH:mm");
  const day = DateTime.fromISO(date?.slice(0, 10) ?? "");
  if (!time.isValid || !day.isValid)
    return DateTime.invalid("Invalid route start");
  return day.set({ hour: time.hour, minute: time.minute });
};

/** One calendar origin for all rows and the clock line, including early live shifts.
 * Recorded activity survives reloads and keeps the origin stable across midnight.
 * Merely planned future routes must retain their scheduled date.
 */
export const getTimelineDate = (
  routes: Pick<AdminGetRoutesResDto, "date" | "state" | "locations">[],
  now = DateTime.now(),
): string => {
  const days: DateTime[] = [];
  for (const route of routes) {
    const scheduled = DateTime.fromISO(route.date?.slice(0, 10) ?? "");
    if (!scheduled.isValid) continue;
    days.push(scheduled.startOf("day"));
    if (route.state !== "live") continue;
    days.push(now.startOf("day"));
    for (const location of route.locations ?? []) {
      if (
        isSkippedStop(location) ||
        !["inProgress", "completed", "partiallyCompleted", "cantPlow"].includes(
          location.driverStatus ?? "",
        )
      )
        continue;
      for (const value of [
        location.driverServiceStartTime,
        location.driverServiceEndTime,
        location.driverStatusLastUpdatedAt,
      ]) {
        const recorded = DateTime.fromISO(value || "");
        if (recorded.isValid && recorded <= now)
          days.push(recorded.startOf("day"));
      }
    }
  }
  return (days.length ? DateTime.min(...days) : now).toISODate()!;
};
